import { Award, ExternalLink, ShieldCheck, Sparkles, GraduationCap } from "lucide-react";

type Cert = {
  name: string;
  issuer: string;
  date: string;
  credentialId?: string;
  credentialUrl?: string;
  skills?: string[];
};

type Group = {
  provider: string;
  icon: typeof ShieldCheck;
  accent: string;
  certs: Cert[];
};

const groups: Group[] = [
  {
    provider: "Cloud & Platform",
    icon: ShieldCheck,
    accent: "from-sky-500/20 to-blue-500/10",
    certs: [
      {
        name: "Microsoft Certified: Azure Fundamentals",
        issuer: "Microsoft",
        date: "Issued Feb 2023",
        credentialId: "I616-3223",
        credentialUrl: "https://www.credly.com/badges/421dba9f-93d0-45dc-98bf-f59be94272d7/linked_in_profile&urlhash=Prx8&isSdui=true&lipi=urn:li:page:d_flagship3_profile_view_base_certifications_details;nS0vw6VeTaGthu4Tx3f6uQ==",
      },
      {
        name: "Visual Studio 2022: First Look",
        issuer: "LinkedIn",
        date: "Issued Feb 2022",
        credentialUrl: "https://www.linkedin.com/in/udaymanvar/details/certifications/",
      },
    ],
  },
  {
    provider: "Agile & Delivery",
    icon: GraduationCap,
    accent: "from-emerald-500/20 to-teal-500/10",
    certs: [
      {
        name: "Registered Scrum Basics™",
        issuer: "Agile Education by Scrum Inc.™",
        date: "Issued Sep 2025",
        credentialId: "RSB-8576465",
        credentialUrl: "https://www.linkedin.com/in/udaymanvar/details/certifications/",
        skills: ["Agile Methodologies", "Scrum"],
      },
      {
        name: "Scrum: The Basics",
        issuer: "LinkedIn",
        date: "Issued Jan 2022",
        credentialUrl: "https://www.linkedin.com/in/udaymanvar/details/certifications/",
        skills: ["Scrum"],
      },
    ],
  },
  {
    provider: "AI & Emerging Tech",
    icon: Sparkles,
    accent: "from-amber-500/20 to-orange-500/10",
    certs: [
      {
        name: "Generative AI Mastermind By Outskill",
        issuer: "Outskill",
        date: "Issued Jun 2025",
        skills: ["Generative AI"],
      },
      {
        name: "What Business Leaders Need to Know about Web3 (+ Metaverse)",
        issuer: "LinkedIn",
        date: "Issued Jun 2022",
        credentialUrl: "https://www.linkedin.com/in/udaymanvar/details/certifications/",
      },
    ],
  },
  {
    provider: "Leadership & Communication",
    icon: Award,
    accent: "from-fuchsia-500/20 to-purple-500/10",
    certs: [
      {
        name: "How to Be an Adaptable Employee during Change and Uncertainty",
        issuer: "LinkedIn",
        date: "Issued Sep 2022",
        credentialUrl: "https://www.linkedin.com/in/udaymanvar/details/certifications/",
      },
      {
        name: "Tech Career Skills: Effective Technical Communication",
        issuer: "LinkedIn",
        date: "Issued Jan 2022",
        credentialUrl: "https://www.linkedin.com/in/udaymanvar/details/certifications/",
      },
    ],
  },
];

const achievements = [
  "Promoted to Technical Lead at Rishabh Software",
  "Mentored 30+ engineers across .NET, Angular and cloud",
  "Delivered many projects across enterprise clients",
  "Active contributor & writer on engineering leadership topics",
];

export function Certifications() {
  return (
    <section id="certifications" className="py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-14 max-w-2xl">
          <p className="font-mono text-sm text-primary">// Certifications & Achievements</p>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-foreground md:text-4xl">
            Credentials that back the craft.
          </h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            A continually growing set of cloud, agile and emerging-tech credentials, alongside
            milestones from years of shipping software and leading teams.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {groups.map((g) => (
            <div
              key={g.provider}
              className="group relative overflow-hidden rounded-xl border border-border bg-surface p-6 transition-smooth hover:-translate-y-1 hover:border-primary/50 hover:shadow-elevated"
            >
              <div
                aria-hidden
                className={`pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-gradient-to-br ${g.accent} blur-2xl transition-smooth group-hover:scale-110`}
              />
              <div className="relative">
                <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary transition-smooth group-hover:bg-primary/20">
                  <g.icon className="h-5 w-5" />
                </div>
                <h3 className="mb-4 font-semibold text-foreground">{g.provider}</h3>
                <ul className="space-y-3">
                  {g.certs.map((c) => (
                    <li
                      key={c.name}
                      className="rounded-lg border border-border/60 bg-background/40 p-3 transition-smooth group-hover:border-primary/20"
                    >
                      <div className="flex items-start gap-3">
                        <Award className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium leading-snug text-foreground">
                            {c.name}
                          </p>
                          <p className="mt-0.5 font-mono text-xs text-muted-foreground">
                            {c.issuer} · {c.date}
                          </p>
                          {c.credentialId && (
                            <p className="mt-1 font-mono text-[11px] text-muted-foreground/80">
                              ID: {c.credentialId}
                            </p>
                          )}
                          {c.skills && c.skills.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {c.skills.map((s) => (
                                <span
                                  key={s}
                                  className="rounded-full border border-border bg-background/60 px-2 py-0.5 font-mono text-[10px] text-muted-foreground"
                                >
                                  {s}
                                </span>
                              ))}
                            </div>
                          )}
                          {c.credentialUrl && (
                            <a
                              href={c.credentialUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary transition-smooth hover:underline"
                            >
                              Show credential
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 rounded-xl border border-border bg-surface p-6 md:p-8">
          <h3 className="mb-5 font-semibold text-foreground">Selected Achievements</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {achievements.map((a) => (
              <div
                key={a}
                className="flex items-start gap-3 rounded-lg border border-border/60 bg-background/40 p-3"
              >
                <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                <p className="text-sm text-muted-foreground">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
